import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  InfoBox,
  RefreshButton,
  Stack,
  Table,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdError, MdShield, MdVisibility } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  guardrailsService,
  PaginatedResult,
  PolicyViolation,
} from "services/guardrailsService";
import { getThemeColor } from "theme/colors";

const PolicyViolations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [violations, setViolations] = useState<PolicyViolation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    loadViolations(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const loadViolations = async (pageToLoad: number) => {
    try {
      setLoading(true);
      const data: PaginatedResult<PolicyViolation> =
        await guardrailsService.getViolations(pageToLoad, pageSize);
      setViolations(data.items);
      setTotal(data.total);
      setError("");
    } catch (err: any) {
      setError(err.response?.data?.error || t("app.violations.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;

  if (loading) {
    return (
      <Stack gap={4}>
        <Card.Root>
          <Card.Body>
            <Text
              textAlign="center"
              color={{ base: "gray.600", _dark: "white" }}
            >
              {t("app.violations.loading")}
            </Text>
          </Card.Body>
        </Card.Root>
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      <Flex justify="space-between" align="center">
        <Heading>{t("app.violations.title")}</Heading>
        <HStack gap={3}>
          <AskStrikeUpButton
            context={t("app.askStrikeUp.context.policyViolations")}
          />
          <RefreshButton
            onClick={() => loadViolations(page)}
            loading={loading}
          />
        </HStack>
      </Flex>

      {error && (
        <InfoBox variant="error">
          <Text color="red.700">{error}</Text>
        </InfoBox>
      )}

      <Card.Root
        borderWidth="1px"
        borderColor={getThemeColor("card.border")}
        borderRadius="md"
        overflow="hidden"
      >
        <Card.Body p={0}>
          {loading ? (
            <Flex p={8} justify="center">
              <Text>{t("app.violations.loadingShort")}</Text>
            </Flex>
          ) : violations.length === 0 ? (
            <VStack gap={4} py={8}>
              <MdShield size={64} color="green" />
              <Text
                fontSize="lg"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.violations.noViolations")}
              </Text>
              <Text
                fontSize="sm"
                color={{ base: "gray.500", _dark: "gray.500" }}
                textAlign="center"
              >
                {t("app.violations.noViolationsDescription")}
              </Text>
            </VStack>
          ) : (
            <Box overflowX="auto">
              <Table.Root minW="700px">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>
                      {t("app.violations.incomingMessage")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.violations.violationReasons")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.violations.blockingResponse")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.violations.policyVersion")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.violations.timestamp")}
                    </Table.ColumnHeader>
                    <Table.ColumnHeader>
                      {t("app.violations.actions")}
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {violations.map((violation) => (
                    <Table.Row
                      key={violation.id}
                      cursor="pointer"
                      onClick={() =>
                        navigate(`/admin/policy-violations/${violation.id}`)
                      }
                    >
                      <Table.Cell>
                        <Text fontSize="sm" fontWeight="medium">
                          {truncateText(violation.incomingMessage, 80)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <VStack align="start" gap={1}>
                          {violation.violationReasons
                            .slice(0, 2)
                            .map((reason, idx) => (
                              <Badge
                                key={idx}
                                colorPalette="red"
                                variant="outline"
                                fontSize="xs"
                              >
                                <HStack gap={1}>
                                  <MdError size={14} />
                                  <Text>{truncateText(reason, 40)}</Text>
                                </HStack>
                              </Badge>
                            ))}
                          {violation.violationReasons.length > 2 && (
                            <Text
                              fontSize="xs"
                              color={{ base: "gray.500", _dark: "gray.400" }}
                            >
                              {t("app.violations.moreReasons", {
                                count: violation.violationReasons.length - 2,
                              })}
                            </Text>
                          )}
                        </VStack>
                      </Table.Cell>
                      <Table.Cell>
                        <Text
                          fontSize="sm"
                          color={{ base: "gray.600", _dark: "gray.400" }}
                        >
                          {truncateText(violation.blockingResponse, 60)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge colorPalette="blue" variant="outline">
                          v{violation.policyVersion}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>
                        <Text fontSize="sm">
                          {formatDate(violation.timestamp)}
                        </Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/admin/policy-violations/${violation.id}`,
                            );
                          }}
                          size="sm"
                          variant="ghost"
                          colorPalette="blue"
                          title={t("app.violations.viewDetails")}
                        >
                          <MdVisibility />
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Card.Body>
      </Card.Root>

      {total > 0 && (
        <HStack justify="space-between" align="center">
          <Text fontSize="sm" color={{ base: "gray.600", _dark: "gray.400" }}>
            {t("app.pagination.showing", {
              start: start + 1,
              end: Math.min(end, total),
              total,
            })}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              {t("app.pagination.previous")}
            </Button>
            <Text fontSize="sm">
              {t("app.pagination.pageInfo", {
                current: currentPage,
                total: totalPages,
              })}
            </Text>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              {t("app.pagination.next")}
            </Button>
          </HStack>
        </HStack>
      )}
    </Stack>
  );
};

export default PolicyViolations;
